/**
 * Badge — small status pill.
 *
 * Replaces dozens of inline `inline-block rounded bg-*-50 px-2 py-0.5
 * text-xs font-medium text-*-700` callsites across the web + admin
 * apps that were drifting on tone (`red-*` vs `danger-*`), padding
 * (`px-2` vs `px-1.5`), corner radius, and font weight.
 *
 *   <Badge variant="danger">banned</Badge>
 *   <Badge variant="success">active</Badge>
 *   <Badge variant="warning" density="compact">3 related</Badge>
 *
 * Variants map to the project palette (no off-palette red/amber/emerald):
 *   - `neutral` (default) — `bg-neutral-100 text-neutral-700`
 *   - `info`              — `bg-primary-50 text-primary-700`
 *   - `success`           — `bg-success-50 text-success-700`
 *   - `warning`           — `bg-warning-50 text-warning-700`
 *   - `danger`            — `bg-danger-50 text-danger-700`
 *
 * Density:
 *   - `default` (default) — `px-2 py-0.5`. The 80% case.
 *   - `compact` — `px-1.5 py-0.5`. For dense tables / inline rows.
 *
 * Semantically a `<span>` — composes with surrounding text. No
 * role / aria — badges are decorative; the label's content carries
 * the meaning. If a badge needs to convey status to screen readers
 * (e.g. "banned"), wrap the surrounding context with an aria-label
 * at that level.
 */

import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export type BadgeVariant = 'neutral' | 'info' | 'success' | 'warning' | 'danger';
export type BadgeDensity = 'default' | 'compact';

export interface BadgeProps {
  variant?: BadgeVariant;
  density?: BadgeDensity;
  children: ReactNode;
  className?: string;
}

const VARIANT: Record<BadgeVariant, string> = {
  neutral: 'bg-neutral-100 text-neutral-700',
  info: 'bg-primary-50 text-primary-700',
  success: 'bg-success-50 text-success-700',
  warning: 'bg-warning-50 text-warning-700',
  danger: 'bg-danger-50 text-danger-700',
};

const DENSITY: Record<BadgeDensity, string> = {
  default: 'px-2 py-0.5',
  compact: 'px-1.5 py-0.5',
};

export function Badge({
  variant = 'neutral',
  density = 'default',
  children,
  className,
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-block rounded text-xs font-medium',
        VARIANT[variant],
        DENSITY[density],
        className,
      )}
    >
      {children}
    </span>
  );
}
